(function () {
  const CATEGORY_MAP = {
    'gummy': 'Gummy',
    'fizzy': 'Fizzy',
    'jelly filled': 'Jelly Filled',
    'bubs': 'Bubs',
    'pencil': 'Pencils',
    'bubblegum': 'Bubblegum',
    'vegetarian': 'Vegetarian',
    'halal': 'Halal',
    'vegan free': 'Vegan Free',
    'gluten free': 'Gluten Free',
  };

  const EXCLUDED_KEYWORDS = ['fizzy belt', '300g jelly filled mix', '500g jelly filled mix', 'bub mix', 'bubs mix', 'choc lick jar', 'fudge', 'chocolate', 'fruit salad', 'marshmallow'];

  // Specific products the shop has asked to keep out of the mix builder.
  // Matched against the product title, trimmed and case-insensitive.
  const BLOCKED_TITLES = [
    '50g cherry pencils',
    '50g apple pencils',
    '50g blue raspberry pencils',
    '50g bubblegum pencils',
    '50g rainbow pencils',
    '50g strawberry pencils',
    '50g millions banana',
    '50g millions bubblegum',
    '50g millions vimto',
    '1x vidal giant strawberry cable',
  ];

  const isBlockedTitle = (title) => BLOCKED_TITLES.includes(String(title || '').trim().toLowerCase());

  const MixBuilder = {
    VERSION: '2.6',
    init() {
      console.info(`[MixBuilder] version ${this.VERSION} — 10 tag categories, live stock, full-catalog scan`);
      document.querySelectorAll('[data-mix-builder]').forEach((el) => {
        this.setup(el);
      });
    },

    loadSweets(el) {
      const script = el.querySelector('[data-mix-sweets]');
      if (!script) return [];
      try {
        const data = JSON.parse(script.textContent);
        return Array.isArray(data) ? data : [];
      } catch (err) {
        console.warn('[MixBuilder] Could not parse sweets JSON:', err, script.textContent.slice(0, 300));
        return [];
      }
    },

    // Paginated scan of a storefront product JSON endpoint (/products.json or
    // /collections/{handle}/products.json), in parallel batches of 4 so each
    // page's results land immediately via onProducts. We request 250 per
    // page but never assume the endpoint honours that — Shopify can clamp
    // the page size, so a "short" page is NOT treated as the end of the
    // catalog. The scan only stops on a genuinely empty page, a fully
    // failed batch, or pages that stop yielding products we haven't
    // already seen (guards against broken pagination repeating itself).
    async scanProductJson(pathBase, onProducts, maxBatches = 26, scannedSet = new Set()) {
      const sep = pathBase.includes('?') ? '&' : '?';
      const PARALLEL = 4;
      let pagesFetched = 0;
      let failedPages = 0;
      let endedOnEmptyPage = false;
      let page = 1;
      let guard = 0;
      let done = false;
      let stalled = 0;
      let lastScanned = 0;

      const fetchPage = async (pg) => {
        try {
          const res = await fetch(`${pathBase}${sep}limit=250&page=${pg}`, {
            headers: { Accept: 'application/json' },
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          return Array.isArray(data && data.products) ? data.products : [];
        } catch (err) {
          console.warn(`[MixBuilder] fetch ${pathBase} page ${pg} failed:`, err && err.message);
          return null; // null = request failed; [] = genuine empty page
        }
      };

      while (!done && guard < maxBatches) {
        guard++;
        const pages = [page, page + 1, page + 2, page + 3];
        const results = await Promise.all(pages.map(async (pg) => {
          let products = await fetchPage(pg);
          if (products === null) products = await fetchPage(pg); // one retry
          if (products === null) return null;
          if (products.length) {
            products.forEach((p) => {
              const key = (p.handle || p.title || '').trim().toLowerCase();
              if (key) scannedSet.add(key);
            });
            onProducts(products);
          }
          return products;
        }));
        results.forEach((r) => { if (r === null) failedPages++; else pagesFetched++; });
        const anyEmpty = results.some((r) => Array.isArray(r) && r.length === 0);
        const allFailed = results.length > 0 && results.every((r) => r === null);
        if (anyEmpty || allFailed) {
          endedOnEmptyPage = anyEmpty;
          done = true;
        } else if (scannedSet.size === lastScanned) {
          stalled++;
          if (stalled >= 2) {
            console.warn(`[MixBuilder] ${pathBase} stopped yielding new products — ending scan at page ${page + PARALLEL - 1}`);
            done = true;
          }
        } else {
          stalled = 0;
        }
        lastScanned = scannedSet.size;
        page += PARALLEL;
      }

      return { pagesFetched, failedPages, scanned: scannedSet.size, endedOnEmptyPage };
    },

    // Fetch every product in the shop (via the shop-wide /products.json
    // endpoint, with a per-collection fallback) and keep only sweets tagged
    // with one of the sweet tags. Liquid's `paginate` is capped server-side,
    // so this client-side scan is what guarantees the full sweet list.
    async fetchCollectionSweets(el, onProgress) {
      const currentHandle = el.dataset.mixHandle;
      const isChocolateMode = el.dataset.mixChocolate === 'true';
      const isFudgeMode = el.dataset.mixFudge === 'true';
      const isJellyMode = el.dataset.mixJelly === 'true';
      const TAGS = isChocolateMode
        ? ['chocolate']
        : isFudgeMode
          ? ['fudge']
          : isJellyMode
            ? ['jelly filled']
            : Object.keys(CATEGORY_MAP); // lowercase keywords
      const all = [];
      const seen = new Set();

      const addPage = (products) => {
        products.forEach((p) => {
          if (!p || !p.title) return;
          if (p.handle === currentHandle) return;
          const key = (p.handle || '').trim().toLowerCase() || p.title.trim().toLowerCase();
          if (!key || seen.has(key)) return;
          const titleLower = (p.title || '').toLowerCase();
          // Block any product with "mix" in the title from being a choice.
          if (titleLower.includes('mix')) return;
          // Block specifically-named products from being a choice.
          if (isBlockedTitle(p.title)) return;
          if (!isChocolateMode && !isFudgeMode && !isJellyMode && EXCLUDED_KEYWORDS.some((kw) => titleLower.includes(kw))) return;
          // Chocolate mode: exclude the Chocolate Mix products themselves, Choc Lick items,
          // and non-chocolate sweet types (pencils, bubblegum) reserved for the standard mix.
          if (isChocolateMode && (titleLower.includes('chocolate mix') || titleLower.includes('choc lick') || titleLower.includes('pencil') || titleLower.includes('bubblegum'))) return;
          // Fudge mode: exclude the Fudge Box/mix products themselves and reserved sweet types.
          if (isFudgeMode && (titleLower.includes('fudge box') || titleLower.includes('fudge mix') || titleLower.includes('pencil') || titleLower.includes('bubblegum'))) return;
          // Jelly mode: exclude the Jelly Filled Mix products themselves, marshmallows and reserved sweet types.
          if (isJellyMode && (titleLower.includes('jelly filled mix') || titleLower.includes('marshmallow') || titleLower.includes('pencil') || titleLower.includes('bubblegum'))) return;
          const tags = Array.isArray(p.tags)
            ? p.tags.map((t) => String(t).toLowerCase().trim().replace(/-/g, ' '))
            : String(p.tags || '').split(',').map((t) => t.toLowerCase().trim().replace(/-/g, ' '));
          if (tags.includes('custom mix')) return;
          // Chocolate mode: only Chocolate-tagged sweets. Fudge mode: tagged OR titled fudge.
          // Normal mode: any matching sweet tag.
          let hasTag;
          if (isFudgeMode) {
            hasTag = tags.some((t) => t.includes('fudge')) || titleLower.includes('fudge');
          } else {
            hasTag = TAGS.some((kw) => tags.some((t) => t.includes(kw)));
            // Standard mix: pencils & bubblegum are selectable even when
            // they're tagged with something else (or nothing at all).
            if (!hasTag && !isChocolateMode && !isJellyMode && (titleLower.includes('pencil') || titleLower.includes('bubblegum'))) {
              hasTag = true;
            }
          }
          if (!hasTag) return;
          seen.add(key);
          const img = Array.isArray(p.images) && p.images.length && p.images[0]
            ? (p.images[0].src || '')
            : (p.featured_image || '');
          // /products.json has no top-level `available` — compute it from variants.
          const available = Array.isArray(p.variants) && p.variants.length
            ? p.variants.some((v) => {
                if (typeof v.available === 'boolean') return v.available;
                if (typeof v.inventory_quantity === 'number') return v.inventory_quantity > 0;
                return true;
              })
            : p.available !== false;
          all.push({
            title: p.title,
            tags: Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(','),
            handle: p.handle,
            available,
            image: img,
          });
        });
        // Re-render progressively so Out of Stock shows as soon as data lands.
        if (onProgress) onProgress(all);
      };

      // Scan the whole shop first; if that endpoint is completely
      // unreachable, fall back to scanning each collection individually.
      const scannedSet = new Set();
      const shopScan = await this.scanProductJson('/products.json', addPage, 26, scannedSet);
      let stats = shopScan;
      if (shopScan.pagesFetched === 0) {
        console.warn('[MixBuilder] /products.json unreachable — falling back to per-collection scans');
        const handles = (el.dataset.allCollections || el.dataset.sweetsCollection || '')
          .split(',').map((h) => h.trim()).filter(Boolean).slice(0, 40);
        for (const handle of handles) {
          const scan = await this.scanProductJson(`/collections/${handle}/products.json`, addPage, 8, scannedSet);
          stats = {
            pagesFetched: stats.pagesFetched + scan.pagesFetched,
            failedPages: stats.failedPages + scan.failedPages,
            scanned: scannedSet.size,
            endedOnEmptyPage: stats.endedOnEmptyPage || scan.endedOnEmptyPage,
          };
        }
      }
      console.info(`[MixBuilder] product scan: ${stats.pagesFetched} pages, ${stats.scanned} products scanned, ${all.length} sweets matched${stats.failedPages ? ` (${stats.failedPages} pages failed)` : ''}`);
      return all;
    },

    // Fetch live inventory for a list of products via Shopify's product JSON endpoint.
    // Availability is taken directly from Shopify's own `available` flag, so it
    // respects inventory policies (stop/continue selling, tracking on/off).
    // Calls onProgress after each chunk so the grid can re-render progressively
    // instead of waiting for every batch to finish.
    async refreshLiveStock(products, onProgress) {
      const byHandle = {};
      products.forEach((p) => { if (p.handle) byHandle[p.handle] = p; });
      const handles = Object.keys(byHandle);
      const CHUNK = 20;
      for (let i = 0; i < handles.length; i += CHUNK) {
        const chunk = handles.slice(i, i + CHUNK);
        const results = await Promise.all(
          chunk.map((handle) =>
            fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null)
          )
        );
        results.forEach((data, idx) => {
          if (!data || typeof data.available !== 'boolean') return;
          byHandle[chunk[idx]].available = data.available;
        });
        if (onProgress) onProgress();
      }
    },

    // Variant mode: the mix product's variants are the sweets. Refresh each
    // variant's availability from Shopify's own `available` flag.
    async refreshVariantStock(el, products) {
      const handle = el.dataset.mixHandle;
      if (!handle) return;
      try {
        const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const data = await res.json();
        if (!Array.isArray(data.variants)) return;
        const byId = new Map(products.map((p) => [p.id, p]));
        data.variants.forEach((v) => {
          const product = byId.get(v.id);
          if (!product || typeof v.available !== 'boolean') return;
          product.available = v.available;
        });
      } catch (err) {
        console.warn('[MixBuilder] Could not refresh live stock:', err);
      }
    },

    // Merge any server-rendered sweets with the full collection fetched
    // client-side, de-duplicating by handle (falling back to title). Fetched
    // data is fresher, so it updates availability/images on duplicates.
    mergeSweets(server, fetched) {
      const result = [];
      const byKey = new Map();
      const add = (p, isFetched) => {
        if (!p) return;
        const key = (p.handle || '').trim().toLowerCase() || p.title.trim().toLowerCase();
        if (!key) return;
        const existing = byKey.get(key);
        if (existing) {
          if (isFetched) {
            existing.available = p.available;
            if (p.image) existing.image = p.image;
          }
          return;
        }
        byKey.set(key, p);
        result.push(p);
      };
      (server || []).forEach((p) => add(p, false));
      (fetched || []).forEach((p) => add(p, true));
      return result;
    },

    buildCategories(products, mixMode) {
      const categories = {};
      const seen = {};
      const dropped = [];
      // Block any product with "mix" in the title from being a choice.
      products = (products || []).filter((p) => !(p.title || '').toLowerCase().includes('mix'));
      // Block specifically-named products from being a choice.
      products = (products || []).filter((p) => !isBlockedTitle(p.title));
      if (mixMode === 'chocolate') {
        categories['Chocolate'] = [];
        products.forEach((p) => {
          const titleLower = (p.title || '').toLowerCase();
          if (titleLower.includes('chocolate mix') || titleLower.includes('choc lick') || titleLower.includes('pencil') || titleLower.includes('bubblegum')) {
            dropped.push(p.title + ' (excluded product)');
            return;
          }
          const tags = Array.isArray(p.tags)
            ? p.tags.map((t) => String(t).toLowerCase().trim().replace(/-/g, ' '))
            : String(p.tags || '').split(',').map((t) => t.toLowerCase().trim().replace(/-/g, ' '));
          if (!tags.some((t) => t.includes('chocolate'))) {
            dropped.push(p.title + ' (not chocolate)');
            return;
          }
          categories['Chocolate'].push(p);
        });
      } else if (mixMode === 'fudge') {
        categories['Fudge'] = [];
        products.forEach((p) => {
          const titleLower = (p.title || '').toLowerCase();
          if (titleLower.includes('fudge box') || titleLower.includes('fudge mix') || titleLower.includes('pencil') || titleLower.includes('bubblegum')) {
            dropped.push(p.title + ' (excluded product)');
            return;
          }
          const tags = Array.isArray(p.tags)
            ? p.tags.map((t) => String(t).toLowerCase().trim().replace(/-/g, ' '))
            : String(p.tags || '').split(',').map((t) => t.toLowerCase().trim().replace(/-/g, ' '));
          if (!tags.some((t) => t.includes('fudge')) && !titleLower.includes('fudge')) {
            dropped.push(p.title + ' (not fudge)');
            return;
          }
          categories['Fudge'].push(p);
        });
      } else if (mixMode === 'paintball') {
        // Fixed colour choices — no tag filtering needed.
        categories['Colours'] = products.slice();
      } else if (mixMode === 'jelly') {
        categories['Jelly Filled'] = [];
        products.forEach((p) => {
          const titleLower = (p.title || '').toLowerCase();
          if (titleLower.includes('jelly filled mix') || titleLower.includes('marshmallow') || titleLower.includes('pencil') || titleLower.includes('bubblegum')) {
            dropped.push(p.title + ' (excluded product)');
            return;
          }
          const tags = Array.isArray(p.tags)
            ? p.tags.map((t) => String(t).toLowerCase().trim().replace(/-/g, ' '))
            : String(p.tags || '').split(',').map((t) => t.toLowerCase().trim().replace(/-/g, ' '));
          if (!tags.some((t) => t.includes('jelly filled'))) {
            dropped.push(p.title + ' (not jelly filled)');
            return;
          }
          categories['Jelly Filled'].push(p);
        });
      } else {
        products.forEach((p) => {
          const tags = Array.isArray(p.tags)
            ? p.tags.map((t) => t.toLowerCase().trim().replace(/-/g, ' '))
            : (p.tags || '').split(',').map((t) => t.toLowerCase().trim().replace(/-/g, ' '));

          const isExcluded = EXCLUDED_KEYWORDS.some((kw) =>
            p.title.toLowerCase().includes(kw) || tags.some((t) => t.includes(kw))
          );
          if (isExcluded) { dropped.push(p.title + ' (excluded)'); return; }

          let assigned = false;
          for (const [keyword, catName] of Object.entries(CATEGORY_MAP)) {
            const matched = tags.some((t) => t.includes(keyword));
            if (matched) {
              if (!categories[catName]) categories[catName] = [];
              if (!seen[catName]) seen[catName] = new Set();
              if (!seen[catName].has(p.title)) {
                seen[catName].add(p.title);
                categories[catName].push(p);
              }
              assigned = true;
            }
          }
          // Pencils & bubblegum are selectable by name even when their tags
          // don't match any category.
          if (!assigned) {
            const titleLower = (p.title || '').toLowerCase();
            if (titleLower.includes('pencil')) {
              if (!categories['Pencils']) categories['Pencils'] = [];
              categories['Pencils'].push(p);
              assigned = true;
            } else if (titleLower.includes('bubblegum')) {
              if (!categories['Bubblegum']) categories['Bubblegum'] = [];
              categories['Bubblegum'].push(p);
              assigned = true;
            }
          }
          if (!assigned) dropped.push(p.title + ' (no matching tag)');
        });
      }
      if (dropped.length) console.warn('[MixBuilder] dropped sweets:', dropped);
      Object.keys(categories).forEach((cat) => categories[cat].sort((a, b) => a.title.localeCompare(b.title)));
      return categories;
    },

    setup(el) {
      const trigger = el.querySelector('[data-mix-trigger]');
      const modal = el.querySelector('[data-mix-modal]');
      const overlay = modal ? modal.querySelector('[data-mix-overlay]') : null;
      const closeBtn = modal ? modal.querySelector('[data-mix-close]') : null;
      const grid = modal ? modal.querySelector('[data-mix-grid]') : null;
      const counter = el.querySelector('[data-mix-counter]');
      const addBtn = el.querySelector('[data-mix-add]');
      const errorEl = el.querySelector('[data-mix-error]');
      const debugEl = el.querySelector('[data-mix-debug]');
      const tabsWrap = modal ? modal.querySelector('[data-mix-tabs]') : null;
      const counterModal = modal ? modal.querySelector('[data-mix-counter-modal]') : null;

      if (!trigger || !modal || !grid) return;

      if (modal.parentNode && modal.parentNode !== document.body) {
        document.body.appendChild(modal);
      }

      const max = parseInt(el.dataset.mixMax, 10) || 5;
      const allowedRaw = el.dataset.mixCategories || 'All';
      const allowedCategories = allowedRaw.split(',').map((c) => c.trim());

      const state = {
        selected: [], // array of sweet objects { title, image }, allows duplicates
        max,
        categories: allowedCategories,
        activeTab: 'All',
        sweetsData: null, // null until first load, then { Gummy: [...], ... }
        loading: false,
        loadFailed: false,
      };

      el._mixState = state;

      let loadStarted = false;
      let lastSig = '';

      const getCount = (title) => state.selected.filter((s) => s.title === title).length;

      const uniqueCount = () => {
        const titles = new Set();
        Object.values(state.sweetsData || {}).forEach((arr) => (arr || []).forEach((s) => titles.add(s.title)));
        return titles.size;
      };

      const updateStatus = (loadedCount) => {
        if (!debugEl) return;
        const shown = uniqueCount();
        if (state.loading) {
          debugEl.textContent = shown > 0 ? `Loading sweets… ${shown} found so far` : 'Loading sweets…';
        } else if (state.loadFailed) {
          debugEl.textContent = 'Could not load sweets — tap "Choose Your Own Mix" to retry';
        } else {
          debugEl.textContent = `Mix builder v${MixBuilder.VERSION}: ${loadedCount != null ? loadedCount : shown} sweets loaded`;
        }
      };

      const renderGrid = () => {
        const data = state.sweetsData || {};
        let items = [];
        if (state.activeTab === 'All') {
          const cats = Object.keys(data);
          const seenTitles = new Set();
          cats.forEach((cat) => {
            (data[cat] || []).forEach((sweet) => {
              if (!seenTitles.has(sweet.title)) {
                seenTitles.add(sweet.title);
                items.push(sweet);
              }
            });
          });
        } else {
          items = data[state.activeTab] || [];
        }

        if (!items.length) {
          lastSig = '';
          if (state.loading) {
            grid.innerHTML = `<div class="mix-loading" role="status" aria-live="polite">
              <span class="mix-loading__spinner" aria-hidden="true"></span>
              <p>Loading sweets…</p>
            </div>`;
            return;
          }
          if (state.loadFailed) {
            grid.innerHTML = `<div class="mix-empty">
              <p>We couldn't load the sweets. Please check your connection and try again.</p>
              <button type="button" class="btn btn--secondary" data-mix-retry>Try again</button>
            </div>`;
            return;
          }
          grid.innerHTML = `<p style="text-align:center;color:var(--color-text-soft);padding:2rem;">No sweets found in this category. Make sure your sweets are tagged Gummy, Fizzy, Jelly Filled, Bubs, Pencils, Bubblegum, Vegetarian, Halal, Vegan Free or Gluten Free.</p>`;
          return;
        }

        // Skip redundant re-renders: progressive fetches often land with no
        // visible change, and a full innerHTML swap would flicker the grid,
        // reload images and reset the scroll position.
        const sig = [
          state.activeTab,
          items.map((s) => `${s.title}:${s.available === false ? '0' : '1'}`).join(','),
          state.selected.map((s) => s.title).join(','),
        ].join('|');
        if (sig === lastSig) return;
        lastSig = sig;

        const scrollParent = grid.parentElement;
        const scrollTop = scrollParent ? scrollParent.scrollTop : 0;
        grid.innerHTML = items
          .map((sweet) => {
            const count = getCount(sweet.title);
            const soldOut = sweet.available === false;
            const img = sweet.color
              ? `<span class="mix-item__img mix-item__color" style="background:${sweet.color};" aria-hidden="true"></span>`
              : sweet.image
                ? `<img class="mix-item__img" src="${sweet.image}" alt="${sweet.title}" loading="lazy">`
                : `<span class="mix-item__img mix-item__img--fallback" aria-hidden="true">🍬</span>`;
            return `<div class="mix-item${count > 0 ? ' is-selected' : ''}${soldOut ? ' is-soldout' : ''}" data-mix-item="${sweet.title}" data-mix-image="${sweet.image}" data-mix-id="${sweet.id || ''}" data-mix-handle="${sweet.handle || ''}">
              <button type="button" class="mix-item__main" data-mix-add-one aria-label="Add ${sweet.title}"${soldOut ? ' disabled' : ''}>
                ${img}
                <span class="mix-item__name">${sweet.title}</span>
              </button>
              ${soldOut
                ? '<span class="mix-item__controls mix-item__controls--soldout"><span class="mix-item__soldout">Out of stock</span></span>'
                : `<span class="mix-item__controls">
                  <button type="button" class="mix-item__minus" data-mix-minus aria-label="Remove one ${sweet.title}"${count === 0 ? ' disabled' : ''}>−</button>
                  <span class="mix-item__count" data-mix-count>${count || ''}</span>
                  <button type="button" class="mix-item__plus" data-mix-plus aria-label="Add one ${sweet.title}"${count >= state.max ? ' disabled' : ''}>+</button>
                </span>`}
            </div>`;
          })
          .join('');
        if (scrollParent) scrollParent.scrollTop = scrollTop;
      };

      const updateTabs = () => {
        if (!tabsWrap) return;
        const catOrder = ['Gummy', 'Fizzy', 'Jelly Filled', 'Bubs', 'Pencils', 'Bubblegum', 'Vegetarian', 'Halal', 'Vegan Free', 'Gluten Free'];
        const existing = Object.keys(state.sweetsData || {});
        const ordered = catOrder.filter((c) => existing.includes(c));
        const others = existing.filter((c) => !catOrder.includes(c)).sort();
        const cats = ['All', ...ordered, ...others];
        tabsWrap.innerHTML = cats
          .map((cat) => {
            const hasItems = cat === 'All' || (state.sweetsData[cat] && state.sweetsData[cat].length > 0);
            return `<button type="button" class="mix-tab${cat === state.activeTab ? ' is-active' : ''}" data-mix-tab="${cat}"${hasItems ? '' : ' disabled'}>${cat}</button>`;
          })
          .join('');
      };

      const updateCounter = () => {
        const count = state.selected.length;
        const text = `${count} / ${state.max} selected`;
        if (counter) counter.textContent = text;
        if (counterModal) counterModal.textContent = text;
        if (addBtn) {
          addBtn.disabled = count !== state.max;
          addBtn.textContent = count === state.max
            ? 'Add Mix to Cart'
            : `Select ${state.max - count} more`;
        }
        if (errorEl) {
          if (count > 0 && count < state.max) {
            errorEl.style.display = '';
            errorEl.textContent = `Please select exactly ${state.max} items (${count}/${state.max})`;
          } else {
            errorEl.style.display = 'none';
          }
        }
      };

      const finishLoad = (loadedCount) => {
        state.loading = false;
        if (uniqueCount() === 0) {
          state.loadFailed = true;
          loadStarted = false; // allow the next open / retry to try again
        }
        updateStatus(loadedCount);
        renderGrid();
        updateCounter();
      };

      // Load the sweet list: renders the server-rendered snapshot instantly,
      // then upgrades it with the full shop-wide fetch (progressively).
      // Idempotent — extra calls are no-ops while a load is in flight/done.
      const beginLoad = () => {
        if (loadStarted) return;
        loadStarted = true;
        state.loading = true;
        state.loadFailed = false;

        const isChocolateMode = el.dataset.mixChocolate === 'true';
        const isFudgeMode = el.dataset.mixFudge === 'true';
        const isPaintballMode = el.dataset.mixPaintball === 'true';
        const isJellyMode = el.dataset.mixJelly === 'true';
        const mixMode = isChocolateMode ? 'chocolate' : isFudgeMode ? 'fudge' : isPaintballMode ? 'paintball' : isJellyMode ? 'jelly' : null;
        if (mixMode === 'chocolate') state.activeTab = 'Chocolate';
        if (mixMode === 'fudge') state.activeTab = 'Fudge';
        if (mixMode === 'paintball') state.activeTab = 'Colours';
        if (mixMode === 'jelly') state.activeTab = 'Jelly Filled';

        const products = MixBuilder.loadSweets(el);
        state.sweetsData = MixBuilder.buildCategories(products, mixMode);
        console.info(`[MixBuilder] loaded ${products.length} sweets from JSON, grouped into ${Object.keys(state.sweetsData).length} categories`);
        updateTabs();
        updateStatus();
        renderGrid();
        updateCounter();

        // Paintball mode uses a fixed colour list — no fetching needed.
        if (isPaintballMode) {
          state.loading = false;
          updateStatus();
          renderGrid();
          return;
        }

        // Fetch every tagged sweet shop-wide via /products.json so the
        // choices aren't limited by Liquid's paginate cap.
        const collHandle = el.dataset.allCollections || el.dataset.sweetsCollection;
        if (collHandle && el.dataset.mixMode !== 'variants') {
          let mergedCount = products.length;
          // Rebuild + re-render progressively as each page of fresh data lands,
          // so Out of Stock registers as fast as possible.
          const rebuild = (fetched) => {
            const merged = MixBuilder.mergeSweets(products, fetched);
            mergedCount = merged.length;
            state.sweetsData = MixBuilder.buildCategories(merged, mixMode);
            updateTabs();
            updateStatus(mergedCount);
            renderGrid();
            updateCounter();
          };
          MixBuilder.fetchCollectionSweets(el, rebuild)
            .then((fetched) => {
              rebuild(fetched);
              finishLoad(mergedCount);
            })
            .catch((err) => {
              console.warn('[MixBuilder] collection fetch failed:', err);
              // Fallback: refresh stock progressively on the server list.
              MixBuilder.refreshLiveStock(products, () => {
                renderGrid();
                updateCounter();
              })
                .catch(() => {})
                .then(() => finishLoad(products.length));
            });
        } else {
          const refresh = el.dataset.mixMode === 'variants'
            ? MixBuilder.refreshVariantStock(el, products)
            : MixBuilder.refreshLiveStock(products, () => {
                renderGrid();
                updateCounter();
              });
          Promise.resolve(refresh)
            .catch((err) => console.warn('[MixBuilder] stock refresh failed:', err))
            .then(() => finishLoad(products.length));
        }
      };

      const retryLoad = () => {
        loadStarted = false;
        state.loading = false;
        state.loadFailed = false;
        state.sweetsData = null;
        lastSig = '';
        beginLoad();
      };

      trigger.addEventListener('click', () => {
        modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        beginLoad();
        renderGrid();
        updateCounter();
      });

      if (overlay) {
        overlay.addEventListener('click', () => {
          modal.classList.remove('is-open');
          document.body.style.overflow = '';
        });
      }

      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          modal.classList.remove('is-open');
          document.body.style.overflow = '';
        });
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('is-open')) {
          modal.classList.remove('is-open');
          document.body.style.overflow = '';
        }
      });

      if (tabsWrap) {
        tabsWrap.addEventListener('click', (e) => {
          const tab = e.target.closest('[data-mix-tab]');
          if (!tab) return;
          tabsWrap.querySelectorAll('.mix-tab').forEach((t) => t.classList.remove('is-active'));
          tab.classList.add('is-active');
          state.activeTab = tab.dataset.mixTab;
          renderGrid();
        });
      }

      grid.addEventListener('click', (e) => {
        if (e.target.closest('[data-mix-retry]')) {
          retryLoad();
          return;
        }
        const item = e.target.closest('[data-mix-item]');
        if (!item) return;
        if (item.classList.contains('is-soldout')) return;
        const title = item.dataset.mixItem;
        const image = item.dataset.mixImage || '';
        const id = item.dataset.mixId ? parseInt(item.dataset.mixId, 10) : null;
        const handle = item.dataset.mixHandle || '';

        if (e.target.closest('[data-mix-minus]')) {
          const idx = state.selected.findIndex((s) => s.title === title);
          if (idx >= 0) state.selected.splice(idx, 1);
        } else if (e.target.closest('[data-mix-plus]')) {
          if (state.selected.length >= state.max) return;
          state.selected.push({ title, image, id, handle });
        } else {
          if (state.selected.length >= state.max) return;
          state.selected.push({ title, image, id, handle });
        }

        renderGrid();
        updateCounter();
      });

      if (addBtn) {
        addBtn.addEventListener('click', async () => {
          if (state.selected.length !== state.max) return;
          const isVariants = el.dataset.mixMode === 'variants';

          let items;
          if (isVariants) {
            items = state.selected
              .filter((s) => s.id)
              .map((s) => ({ id: s.id, quantity: 1 }));
          } else {
            const variantInput = document.querySelector('[data-product-form] [data-variant-id]');
            const variantId = variantInput ? parseInt(variantInput.value, 10) : null;
            if (!variantId) return;

            // Re-verify selected sweets are still in stock right before adding.
            addBtn.classList.add('btn--loading');
            addBtn.disabled = true;
            const soldOutTitles = [];
            const soldOutHandles = new Set();
            await Promise.all(state.selected
              .filter((s) => s.handle)
              .map((s) => fetch(`/products/${s.handle}.js`, { headers: { Accept: 'application/json' } })
                .then((r) => (r.ok ? r.json() : null))
                .catch(() => null)
                .then((data) => {
                  if (data && data.available === false) {
                    soldOutTitles.push(data.title || s.title);
                    soldOutHandles.add(s.handle);
                  }
                })));
            if (soldOutTitles.length) {
              // Reflect the sold-out state in the grid.
              Object.keys(state.sweetsData || {}).forEach((cat) => {
                (state.sweetsData[cat] || []).forEach((sw) => {
                  if (soldOutHandles.has(sw.handle)) sw.available = false;
                });
              });
              renderGrid();
              updateCounter();
              if (errorEl) {
                errorEl.style.display = '';
                errorEl.textContent = `Sorry, ${soldOutTitles.join(', ')} just sold out — please pick a different sweet.`;
              }
              Branzos.showToast('A selected sweet just sold out', '😵');
              addBtn.classList.remove('btn--loading');
              addBtn.disabled = false;
              updateCounter();
              return;
            }
            addBtn.classList.remove('btn--loading');

            const properties = {};
            const propLabel = el.dataset.mixPaintball === 'true' ? 'Colour' : 'Sweet';
            state.selected.forEach((sweet, i) => {
              properties[`${propLabel} ${i + 1}`] = sweet.title;
            });
            items = [{ id: variantId, quantity: 1, properties }];
          }
          if (!items.length || !window.ShopifyCart) return;

          addBtn.classList.add('btn--loading');
          addBtn.disabled = true;

          fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items }),
          })
            .then((res) => {
              if (!res.ok) throw new Error('add failed');
              return window.ShopifyCart.refresh();
            })
            .then(() => {
              modal.classList.remove('is-open');
              document.body.style.overflow = '';
              if (window.ShopifyTheme && window.ShopifyTheme.cartDrawerEnabled) {
                window.ShopifyCart.openDrawer();
              } else {
                Branzos.showToast('Mix added to cart!', '🛒');
              }
              state.selected = [];
              renderGrid();
              updateCounter();
            })
            .catch(() => {
              Branzos.showToast('Could not add mix to cart', '😵');
            })
            .finally(() => {
              addBtn.classList.remove('btn--loading');
              addBtn.disabled = false;
              updateCounter();
            });
        });
      }

      // Warm the sweet list before the customer clicks so the modal is
      // ready the moment it opens: kick off the load when the builder is
      // about to scroll into view, on hover/touch, and on the click itself.
      const kick = () => beginLoad();
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries, obs) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            obs.disconnect();
            kick();
          }
        }, { rootMargin: '400px 0px' });
        io.observe(trigger);
      } else {
        kick();
      }
      trigger.addEventListener('pointerenter', kick);
    },
  };

  window.MixBuilder = MixBuilder;
  Branzos.onLoad(() => MixBuilder.init());
})();
